import type { TPendingImport } from "containers/BulkUpdatesContainer/ConfirmationStep";
import type { IChannel } from "containers/Channels/types";
import { getLegacyMilestoneBaseColumn } from "containers/TermFormContainer/ChannelsStep/milestoneBaseColumnUtils";
import type { INameTranslation } from "containers/Titles/types";
import { getCountries } from "countries";
import {
    convertAnyAsArray,
    frenchDateRegex,
    generateRandomIssn,
    getCleanStartorEndDay,
    isDateFormatValid,
    isObjectEmpty,
    normalizePercentage,
    removeAccents,
    removeAllSpaces,
    removeVatFromPrice,
    replaceComma,
} from "helpers/utils";
import { resolveArticleCalculationBase } from "helpers/pressCalculationBase";
import { t } from "i18next";
import type { Dispatch, SetStateAction } from "react";
import { ArticleCompletionStatus } from "store/articleStore";
import type { ICustomField } from "store/customFieldsStore";
import type { TImportError } from "store/dataImportSlice";
import type { Job } from "store/jobsStore";
import { ProcessingJobStatus } from "store/jobsStore";
import type { TPeriod } from "store/periodStore";
import { ChannelKind, ChannelType } from "store/types";
import type { TImportTargets } from "types";

import type { CurrentChannel, TImportDataDictionary, TObj } from "./type";

export const advancePaymentTransform = (data: TObj[]) => {
    return data
        .filter((e) => !!Object.keys(e).length)
        .map((line) => {
            const advance: Record<string, any> = { ...line };
            if (Array.isArray(line.amount)) {
                advance.amount = advance.amount.map((amount: any) => {
                    if (typeof amount === "string") {
                        amount = amount.replace(",", ".");
                    }
                    return +amount * 100;
                });
                advance.paidAt = advance.paidAt.map((date) => {
                    return date ? getCleanStartorEndDay(date).toLocaleDateString("en-CA") : "";
                });
            } else {
                if (typeof advance.amount === "string") {
                    advance.amount = advance.amount.replace(",", ".");
                }
                advance.amount = +advance.amount * 100;
                advance.paidAt = advance.paidAt
                    ? getCleanStartorEndDay(advance.paidAt).toLocaleDateString("en-CA")
                    : "";
            }

            return advance;
        });
};

function formatCountryNames(name: string) {
    const nameWithoutAccents = removeAccents(name?.toLowerCase());
    return nameWithoutAccents?.replace(/-/g, " ");
}

/**
 * Reads a numeric column of an imported file, whatever separator and spacing it uses.
 * Returns null when the column is absent or unreadable, so the backend keeps its own fallback.
 */
const parseImportedNumber = (value: unknown, { inCents = false }: { inCents?: boolean } = {}): number | null => {
    if (value === undefined || value === null || value === "") return null;

    const parsedValue = replaceComma(removeAllSpaces(value.toString()));
    if (isNaN(parsedValue)) return null;

    return inCents ? Math.round(parsedValue * 100) : parsedValue;
};

const saleItemValidator = (item: any, index: number, addError: (error: Record<string, any>) => void) => {
    if (item.distributorInvoicingPercentage) {
        try {
            item.distributorInvoicingPercentage = normalizePercentage(item.distributorInvoicingPercentage);
        } catch (e) {
            const newError = {
                entry: item,
                fileLine: index + 2,
                mappedKey: "distributorInvoicingPercentage",
                message: `${t("ImportData.InvalidPercentage")}`,
            };
            addError?.(newError);
        }
    }

    if (item.turnover) {
        item.turnover = removeAllSpaces(item.turnover);

        if (!item.turnover || isNaN(parseFloat(item.turnover))) {
            const newError = {
                entry: item,
                fileLine: index + 2,
                mappedKey: "turnover",
                message: `${t("ImportData.InvalidTurnover")}`,
            };
            addError?.(newError);
        }
    }

    if (item.distributorGrossPrice) {
        if (isNaN(parseFloat(item.distributorGrossPrice))) {
            const newError = {
                entry: item,
                fileLine: index + 2,
                mappedKey: "distributorGrossPrice",
                message: t("ImportData.InvalidDistributorGrossPrice"),
            };
            addError?.(newError);
        }
        item.distributorGrossPrice = Math.round(
            +removeAllSpaces(item.distributorGrossPrice.toString().replace(",", ".")) * 100,
        );
    }

    /**
     * Custom turnover columns are stored in cents, like every other monetary column. The async
     * upload converts them in SQL when projecting the staging table, this legacy path converts here.
     */
    (["turnover_1", "turnover_2"] as const).forEach((customTurnoverKey) => {
        if (!item[customTurnoverKey]) return;

        const rawValue = removeAllSpaces(item[customTurnoverKey].toString()).replace(",", ".");

        if (isNaN(parseFloat(rawValue))) {
            addError?.({
                entry: item,
                fileLine: index + 2,
                mappedKey: customTurnoverKey,
                message: `${t("ImportData.InvalidTurnover")}`,
            });
            return;
        }

        item[customTurnoverKey] = Math.round(+rawValue * 100);
    });

    return item;
};

export const salesTransform = (
    data: TObj[],
    channels: Partial<IChannel>[],
    currentChannel: CurrentChannel,
    activePeriods: TPeriod[],
    usesMultiPeriods: boolean,
    setFileErrors?: Dispatch<SetStateAction<Record<string, any>[]>>,
) => {
    const countryCodesArr = getCountries();

    const countryCodesMap = countryCodesArr.reduce<Record<string, string>>((acc, country) => {
        const countryName = formatCountryNames(country.name);
        acc[countryName] = country.code;
        return acc;
    }, {});

    const fileErrors: Array<Record<string, any>> = [];

    const transformedData = data
        .filter((e) => !!Object.keys(e).length)
        .map((item: any, index) => {
            let activeChannel: Partial<IChannel> | undefined;
            if (item.channelId) {
                activeChannel = channels.find(
                    (channel) => channel?.customIdentifier == item.channelId || channel.id === +item.channelId,
                );
            } else {
                activeChannel = channels.find((channel) => channel.id == currentChannel.id);
            }

            // We set error for channelId here, because message sent for backend (Missing resource) isn't clear for client.
            if (!activeChannel) {
                const newError = {
                    entry: item,
                    fileLine: index + 2,
                    mappedKey: "channelId",
                    message: t("ImportData.InexistentChannel"),
                };

                fileErrors.push(newError);
            }

            if (activeChannel?.kind === ChannelKind.COLLECTIVE_MANAGEMENT) {
                const cmKind = item.collectiveManagementKind?.toUpperCase() ?? undefined;
                if (cmKind) item.collectiveManagementKind = cmKind;
                if (!cmKind) {
                    fileErrors.push({
                        entry: item,
                        fileLine: index + 2,
                        mappedKey: "collectiveManagementKind",
                        message: t("ServerError.MissingCollectiveManagementKind"),
                    });
                } else if (!["SOFIA", "CFC", "OTHER"].includes(cmKind)) {
                    fileErrors.push({
                        entry: item,
                        fileLine: index + 2,
                        mappedKey: "collectiveManagementKind",
                        message: t("ServerError.InvalidCollectiveManagementKind"),
                    });
                }
            }

            const countryCode = countryCodesMap[formatCountryNames(item.countryMarket)] ?? item.countryMarket;

            item = saleItemValidator(item, index, (err) => {
                fileErrors.push(err);
            });

            const hasOverridenPrice = !!item?.overridenPrice;

            // firstDay not used on multiperiods, so we can use the first period of array:
            const firstDayOfPeriod = activePeriods[0].periodStart;

            // If they specified a date, check if it's valid otherwise add error.
            let importDate = item.date;

            if (item.date) {
                const isDateValid = isDateFormatValid({ date: importDate });
                const newError = {
                    entry: item,
                    fileLine: index + 2,
                    mappedKey: "date",
                    message: t("ServerError.InvalidDateFormat"),
                };

                if (isDateValid) {
                    importDate = getCleanStartorEndDay(importDate).toLocaleDateString("en-CA");
                } else {
                    importDate = undefined;
                    fileErrors.push(newError);
                }
            }

            return {
                ...item,
                // If they don't use Multiperiods, set fallback date otherwise if no date specified, otherwise add nothing to trigger error.
                // I have to convert to string to avoid JSON causing problem with UTC.
                date: !usesMultiPeriods
                    ? importDate
                        ? importDate
                        : getCleanStartorEndDay(firstDayOfPeriod).toLocaleDateString("en-CA")
                    : importDate,
                countryMarket: countryCode,
                returnedSales: removeAllSpaces(item.returnedSales) ?? "",
                /**
                 * Calculation-base columns of the line: a sale can fill several of them and each
                 * contract picks the one it computes on. Turnover is sent in cents like every other
                 * monetary column, quantities stay whole units.
                 */
                quantity: parseImportedNumber(item.quantity),
                turnover: parseImportedNumber(item.turnover, { inCents: true }),
                overridenPrice: hasOverridenPrice
                    ? Math.round(+item?.overridenPrice?.toString().replace(",", ".") * 100) || ""
                    : null,
                overridenPriceCurrency: hasOverridenPrice ? item?.overridenPriceCurrency ?? "" : null,
            };
        });

    setFileErrors?.((prev) => [...prev, ...fileErrors]);

    return transformedData;
};

/**
 * Milestone thresholds are stored in cents when the imported milestone base is a monetary column.
 * Bases come from the import row; a row that carries none (mass update of existing contracts) keeps
 * the historical unit of the channel, which is the base the contracts were migrated to.
 */
const shouldMultiplyMilestoneThresholds = (
    channel: Partial<IChannel> | undefined,
    milestoneBaseColumn?: string | null,
    calculationBaseColumn?: string | null,
): boolean => {
    const importedBase = milestoneBaseColumn || calculationBaseColumn;
    const resolvedMilestoneBase = importedBase
        ? importedBase.trim().toLowerCase()
        : getLegacyMilestoneBaseColumn(channel);
    return resolvedMilestoneBase !== "quantity";
};

const getRepeatedImportValue = (term: Record<string, any>, key: string, index: number): string | null => {
    const value = term[key];
    if (Array.isArray(value)) {
        const entry = value[index];
        return entry == null || entry === "" ? null : String(entry);
    }
    return value == null || value === "" ? null : String(value);
};

export const termTransform = (data: TObj[], channels: Partial<IChannel>[], isUpdate = false) => {
    return data
        .filter((e) => !!Object.keys(e).length)
        .map((item) => {
            const term: any = { ...item };

            const valuesToFormat = ["initialAdvance", "initialBalance", "initialProvision", "cumulativeRights"];

            valuesToFormat.forEach((key) => {
                if (key in term) {
                    term[key] = Math.round((+replaceComma(term[key]) || 0) * 100);
                }
            });

            const hasChannelId = !isUpdate || (isUpdate && term.channelId);

            if (hasChannelId && Array.isArray(term.channelId)) {
                term.channelId.forEach((channelId: number, index: number) => {
                    const channel = channels.find(
                        (channel) => channel?.customIdentifier == String(channelId) || channel.id == channelId,
                    );

                    if (channel) {
                        const multiplyMilestones = shouldMultiplyMilestoneThresholds(
                            channel,
                            getRepeatedImportValue(term, "milestoneBaseColumn", index),
                            getRepeatedImportValue(term, "calculationBaseColumn", index),
                        );
                        for (let i = 1; i <= 5; i++) {
                            if (
                                Array.isArray(term[`channelMilestone_${i}`]) &&
                                Array.isArray(term[`channelPercentage_${i}`])
                            ) {
                                if (term[`channelPercentage_${i}`][index] != undefined)
                                    term[`channelPercentage_${i}`][index] = replaceComma(
                                        term[`channelPercentage_${i}`][index],
                                    ).toString();
                                if (
                                    multiplyMilestones &&
                                    term[`channelMilestone_${i}`][index] &&
                                    term[`channelPercentage_${i}`][index]
                                ) {
                                    if (term[`channelMilestone_${i}`][index].toLowerCase() !== "infinity") {
                                        term[`channelMilestone_${i}`][index] = Math.round(
                                            +term[`channelMilestone_${i}`][index] * 100,
                                        ).toString();
                                    }
                                }
                            } else {
                                if (term[`channelPercentage_${i}`] != undefined)
                                    term[`channelPercentage_${i}`] = replaceComma(
                                        term[`channelPercentage_${i}`],
                                    ).toString();
                                if (
                                    multiplyMilestones &&
                                    term[`channelMilestone_${i}`] &&
                                    term[`channelPercentage_${i}`]
                                ) {
                                    if (term[`channelMilestone_${i}`].toLowerCase() !== "infinity") {
                                        term[`channelMilestone_${i}`] = Math.round(
                                            +term[`channelMilestone_${i}`] * 100,
                                        ).toString();
                                    }
                                }
                            }
                        }
                    }
                });
            } else if (hasChannelId && !Array.isArray(term.channelId)) {
                // channelId not an array
                const channel = channels.find(
                    (channel) => channel?.customIdentifier == String(term.channelId) || channel.id == term.channelId,
                );
                if (channel) {
                    const multiplyMilestones = shouldMultiplyMilestoneThresholds(
                        channel,
                        getRepeatedImportValue(term, "milestoneBaseColumn", 0),
                        getRepeatedImportValue(term, "calculationBaseColumn", 0),
                    );
                    for (let i = 1; i <= 5; i++) {
                        if (term[`channelPercentage_${i}`] != undefined)
                            term[`channelPercentage_${i}`] = replaceComma(term[`channelPercentage_${i}`]).toString();
                        if (multiplyMilestones && term[`channelMilestone_${i}`] && term[`channelPercentage_${i}`]) {
                            if (term[`channelMilestone_${i}`].toLowerCase() !== "infinity") {
                                term[`channelMilestone_${i}`] = Math.round(
                                    +term[`channelMilestone_${i}`] * 100,
                                ).toString();
                            }
                        }
                    }
                }
            }

            return term;
        });
};

export const publicationWithArticleTransform = (data: TObj[], usesBothChannels: boolean, preferredChannel: string) => {
    return data
        .filter((e) => !!Object.keys(e).length)
        .map((title) => {
            const { grossDuty, folioPrice, folioQuantity, channelId } = title;
            const articleChannelId = usesBothChannels ? channelId : preferredChannel;
            const articleCompletionStatus =
                title.articleCompletionStatus == 1
                    ? ArticleCompletionStatus.COMPLETED
                    : ArticleCompletionStatus.PENDING;

            return {
                ...title,
                articleCompletionStatus,
                channelId: articleChannelId,
                grossDuty: grossDuty
                    ? +removeAllSpaces((grossDuty as string).replace(",", ".")) * 100 || ""
                    : undefined,
                folioPrice: folioPrice
                    ? +removeAllSpaces((folioPrice as string).replace(",", ".")) * 100 || ""
                    : undefined,
                folioQuantity: folioQuantity
                    ? +removeAllSpaces((folioQuantity as string).replace(",", "."))
                    : undefined,
            };
        });
};

/** Same default (100 = 1€) as publication form when price field is hidden */
export const getPublicationChannelPrice = (item: TObj): number =>
    item.packagePrice != null && String(item.packagePrice).trim().length > 0
        ? Math.round(+item.packagePrice * 100)
        : 100;

/**
 * Mapping keeps CSV headers + i18n labels + API keys on each row.
 * Call this once before any import POST so only mapped API keys (and custom fields) are sent.
 */
const MAPPING_PLACEHOLDERS = new Set(["NotMandatory", "Ignore"]);

/** Keeps file columns the mapper did not consume so the API can reject them by name. */
export const withUnmappedColumns = (
    sourceRows: TObj[],
    mappedRows: TObj[],
    allowedKeys: readonly string[],
    knownLabels: readonly string[],
    mappedSourceHeaders: readonly string[] = [],
): TObj[] => {
    const allowed = new Set(allowedKeys);
    const labels = new Set(knownLabels);
    const consumed = new Set(mappedSourceHeaders.filter((header) => !MAPPING_PLACEHOLDERS.has(header)));
    return mappedRows.map((mapped, index) => {
        const source = sourceRows[index] ?? {};
        const next = { ...mapped };
        for (const key of Object.keys(source)) {
            if (allowed.has(key) || labels.has(key) || consumed.has(key) || key.startsWith("meta:")) continue;
            if (next[key] !== undefined) continue;
            next[key] = source[key];
        }
        return next;
    });
};

export const pickMappedImportFields = (rows: TObj[], allowedKeys: readonly string[]): TObj[] => {
    const allowed = new Set(allowedKeys);
    return rows.map((row) => {
        const picked: TObj = {};
        for (const key of Object.keys(row)) {
            if (allowed.has(key) || key.startsWith("meta:")) {
                picked[key] = row[key];
            }
        }
        return picked;
    });
};

export const publicationTransform = (data: TObj[]) => {
    return data
        .filter((e) => !!Object.keys(e).length)
        .map((title) => {
            const {
                prize,
                summary,
                draw,
                format,
                pagination,
                distributorReference,
                deliveryDate,
                parutionCode,
                issn,
                ...rest
            } = title;
            return {
                ...rest,
                name: (title.name as string)?.trim() ?? "",
                ean13: issn || title.issueUniqueCode || generateRandomIssn(),
                price: Math.round((+title.price || 0) * 100),
                channelPrice: getPublicationChannelPrice(title),
                otherInformation: {
                    deliveryDate: frenchDateRegex.test(deliveryDate as string)
                        ? (deliveryDate as string).split("/").reverse().join("-")
                        : deliveryDate,
                    parutionCode,
                },
            };
        });
};

const formatCustomFieldsImport = (data, customFields): Record<string, string> => {
    const customFieldKeys = Object.keys(data).filter((item) => item.includes("meta:"));
    const formattedCustomFields = {};

    if (customFieldKeys.length > 0) {
        customFieldKeys.forEach((key) => {
            let value = data[key];
            const currentField = customFields.find((item) => item.uuid === key.replace("meta:", ""));
            if (currentField.valueType === "boolean") {
                value = +data[key];
            }
            formattedCustomFields[key.replace("meta:", "")] = value;
        });
    }

    return formattedCustomFields;
};

/**
 * Formats the name translations from a title object.
 * @param title - The title object to format the name translations from.
 * @returns The name translations formatted as an array of INameTranslation objects.
 */
const formatNameTranslationsFromTitle = (title: TObj): INameTranslation[] => {
    const nameTranslations: INameTranslation[] = [];
    const translationKeys = Object.keys(title).filter((key) => key.includes("name_")); // name_fr, name_en, etc.
    translationKeys.forEach((key) => {
        const value = title[key];
        // Filter out empty values to prevent creating translations with empty names
        if (value && String(value).trim()) {
            nameTranslations.push({
                locale: key.replace("name_", ""),
                name: String(value).trim(),
            });
        }
    });
    return nameTranslations;
};

export const titleTransform = ({
    data,
    usesVat = false,
    channels,
    customFields,
    isUpdate = false,
    companyCountryCode = "FR",
}: {
    data: TObj[];
    usesVat?: boolean;
    channels: Partial<IChannel>[];
    isUpdate?: boolean;
    customFields: ICustomField[];
    companyCountryCode?: string;
}) => {
    return data
        .filter((e) => !!Object.keys(e).length)
        .map((title, index) => {
            const {
                prize,
                summary,
                draw,
                format,
                pagination,
                category,
                formatType,
                formatTypeName,
                formatTypeId,
                ...rest
            } = title;
            const channelIds = convertAnyAsArray(title.channelId).map((id) => +id);
            const currency = title.currency || "EUR";
            const channelPrices = convertAnyAsArray(title.channelPrice);
            const nameTranslations = formatNameTranslationsFromTitle(title);

            if (title.name) {
                rest.name = (title.name as string)?.trim() ?? "";
            }

            if (nameTranslations.length > 0) {
                rest.nameTranslations = nameTranslations;
            }

            if (Array.isArray(rest.cumulativeSales)) {
                rest.cumulativeSales = rest.cumulativeSales.map((e, i) => {
                    const channel = channels.find(
                        (channel) =>
                            channel.id == channelIds[i] || +(channel.customIdentifier ?? "-1") === channelIds[i],
                    );
                    const isNormalCAChannel =
                        channel?.type === ChannelType.AMOUNT_BASED && !channel.usesAlternativeMilestoneAmount;
                    return isNormalCAChannel && e ? Math.round(replaceComma(e) * 100) : e;
                }) as [string, number];
            } else {
                const channel = channels.find(
                    (channel) => channel?.customIdentifier == String(channelIds[0]) || channel.id == +channelIds[0],
                );

                const isNormalCAChannel =
                    channel?.type === ChannelType.AMOUNT_BASED && !channel.usesAlternativeMilestoneAmount;

                rest.cumulativeSales =
                    isNormalCAChannel && rest.cumulativeSales
                        ? Math.round(replaceComma(rest.cumulativeSales as string) * 100)
                        : rest.cumulativeSales;
            }

            // Independent monetary opening cumulatives are always stored in cents.
            (["turnover", "turnover_1", "turnover_2"] as const).forEach((column) => {
                if (rest[column] === undefined || rest[column] === null || rest[column] === "") return;

                if (Array.isArray(rest[column])) {
                    rest[column] = (rest[column] as unknown[]).map((value) =>
                        value === "" || value === null || value === undefined
                            ? value
                            : Math.round(replaceComma(value as string) * 100),
                    );
                } else {
                    rest[column] = Math.round(replaceComma(rest[column] as string) * 100);
                }
            });

            if (title.price) {
                rest.price =
                    Math.round(
                        (usesVat && currency == "EUR"
                            ? removeVatFromPrice(replaceComma(title.price as string), companyCountryCode)
                            : replaceComma(title.price as string)) * 100,
                    ) || 0;
            }

            const dataHasCustomFields = Object.keys(rest).some((key) => key.includes("meta:"));
            if (dataHasCustomFields) rest.customFields = formatCustomFieldsImport(title, customFields) as any;

            // When importing summaries, they might add quotes at start and end for CSV purposes.
            // We remove them before saving data.
            let cleanSummary = (summary as string) ?? "";
            if (cleanSummary.startsWith('"') && cleanSummary.endsWith('"')) {
                cleanSummary = cleanSummary.slice(1, -1);
            }

            const otherInformation = { prize, summary: cleanSummary, draw, format, pagination, category };
            if (!isUpdate || (isUpdate && !isObjectEmpty(otherInformation)))
                rest.otherInformation = otherInformation as any;

            const importedFormatTypeName = String(formatTypeName ?? formatType ?? "").trim();
            if (importedFormatTypeName) {
                rest.formatTypeName = importedFormatTypeName;
            }
            if (formatTypeId !== undefined && formatTypeId !== null && String(formatTypeId).trim() !== "") {
                rest.formatTypeId = Number(formatTypeId);
            }

            const channelPrice = channelPrices.map((channelPrice, i) => {
                if (channelPrice === "") {
                    return null;
                } else {
                    const channelCurrency = title.channelCurrency?.[i] || "EUR";
                    return Math.round(
                        (usesVat && channelCurrency == "EUR"
                            ? removeVatFromPrice(replaceComma(channelPrice) || 0, companyCountryCode)
                            : replaceComma(channelPrice) || 0) * 100,
                    );
                }
            });
            if (Object.prototype.hasOwnProperty.call(title, "channelPrice") && (!isUpdate || channelPrice.length > 0)) {
                rest.channelPrice = channelPrice;
            }

            if (title.channelSubscriptionCoefficient) {
                const subscriptionCoefficients = convertAnyAsArray(title.channelSubscriptionCoefficient);
                rest.channelSubscriptionCoefficient = subscriptionCoefficients.map((coef) => {
                    if (coef === "" || coef === null || coef === undefined) {
                        return null;
                    }
                    return replaceComma(coef);
                });
            }

            return {
                ...rest,
            };
        });
};

const INTL_BANKING_FIELDS = [
    "bank_account_country",
    "intl_swift",
    "intl_account_number",
    "intl_routing_code",
    "intl_bank_name",
] as const;

export const beneficiaryTransform = ({
    data,
    update = false,
    customFields,
    bankingFields,
    internationalBankingEnabled = false,
}: {
    data: TObj[];
    update?: boolean;
    customFields: ICustomField[];
    /** Country banking fields from countryFeatures — used to nest non-IBAN fields into bankingMetadata */
    bankingFields?: string[];
    /** When true, also nests international banking fields into bankingMetadata */
    internationalBankingEnabled?: boolean;
}) => {
    const metaBankingFields = bankingFields?.filter((f) => f !== "iban" && f !== "bic") ?? [];

    return data
        .filter((e) => !!Object.keys(e).length)
        .map((beneficiary) => {
            const req = structuredClone(beneficiary);
            const dataHasCustomFields = Object.keys(req).some((key) => key.includes("meta:"));
            // On updates, only return customFields if a column matches meta
            // otherwise, not include.
            if (dataHasCustomFields) req.customFields = formatCustomFieldsImport(beneficiary, customFields) as any;
            if (update) req.id = +req["id"];
            if (beneficiary.name) req.name = (beneficiary.name as string)?.trim() ?? "";
            if (beneficiary.surname) req.surname = (beneficiary.surname as string)?.trim() ?? "";
            if (beneficiary.pseudonym) req.pseudonym = (beneficiary.pseudonym as string)?.trim() ?? "";

            // Nest non-IBAN banking fields into bankingMetadata as the backend expects.
            // Only include fields with non-empty values — empty strings fail backend validation.
            const presentMetaFields = metaBankingFields.filter((f) => f in req && req[f] !== "" && req[f] != null);
            const presentIntlFields = internationalBankingEnabled
                ? INTL_BANKING_FIELDS.filter((f) => f in req && req[f] !== "" && req[f] != null)
                : [];
            if (presentMetaFields.length > 0 || presentIntlFields.length > 0) {
                const bankingMetadata: Record<string, string> = (req as Record<string, any>).bankingMetadata ?? {};
                for (const f of presentMetaFields) {
                    bankingMetadata[f] = req[f] as string;
                    delete req[f];
                }
                for (const f of presentIntlFields) {
                    bankingMetadata[f] = req[f] as string;
                    delete req[f];
                }
                (req as Record<string, any>).bankingMetadata = bankingMetadata;
            }
            // Always clean up meta fields from the top level even if empty
            for (const f of metaBankingFields) {
                if (f in req) delete req[f];
            }
            if (internationalBankingEnabled) {
                for (const f of INTL_BANKING_FIELDS) {
                    if (f in req) delete req[f];
                }
            }

            return req;
        });
};

export const articleTransform = (
    data: TObj[],
    channels: Partial<IChannel>[],
    usesBothChannels: boolean,
    preferredChannel: string,
) => {
    return data.map((article) => {
        // If they use both channels, we use the Id they passed in the file. If not, we use the preferredChannel (from settings).
        const channelId = usesBothChannels ? +article.channelId : +preferredChannel;
        const channel = channels.find((item) => item.id === channelId);
        const calculationBase = resolveArticleCalculationBase({ channel });
        const isAmountBased = calculationBase === "amount";
        const completionStatus =
            article.completionStatus == 1 ? ArticleCompletionStatus.COMPLETED : ArticleCompletionStatus.PENDING;

        const packageQuantity = () => {
            if (isAmountBased) {
                return replaceComma(article.grossDuty as string) * 100;
            }
            return replaceComma(article.packageQuantity as string);
        };

        return {
            ...article,
            packageQuantity: packageQuantity(),
            packagePrice: replaceComma((article?.packagePrice as string) ?? "0") * 100,
            channelId,
            calculationBase,
            completionStatus,
        };
    });
};

export const stockTransform = (data: TObj[]) => {
    return data.map<TObj>((line) => ({
        ...line,
        inventory: removeAllSpaces(line.inventory as string).replace(",", "."),
        pressInventory: removeAllSpaces(line.pressInventory as string).replace(",", "."),
        scrappedInventory: removeAllSpaces(line.scrappedInventory as string).replace(",", "."),
        printedInventory: removeAllSpaces(line.printedInventory as string).replace(",", "."),
    }));
};

export const provisionTransform = (data: TObj[]) => {
    const fileErrors: Array<{
        entry: (typeof data)[0];
        fileLine: number;
        mappedKey: keyof (typeof data)[0];
        message: string;
    }> = [];

    const transformedData = [...data]
        .filter((line) => !isObjectEmpty(line))
        .map<TObj>((line, i) => {
            const { reinstatedProvision: rawReinstate, ...restLine } = line as any;
            let result: TObj;
            try {
                result = {
                    ...restLine,
                    provision: normalizePercentage(
                        removeAllSpaces(line.provision as string).replace(",", "."),
                    ).toString(),
                };
            } catch (e) {
                fileErrors.push({
                    entry: line,
                    fileLine: i + 2,
                    mappedKey: "provision",
                    message: `${t("ImportData.InvalidPercentage")}`,
                });
                return line;
            }

            if (rawReinstate !== undefined && rawReinstate !== null && rawReinstate !== "") {
                try {
                    result.reinstatedProvision = normalizePercentage(
                        removeAllSpaces(rawReinstate as string).replace(",", "."),
                    ).toString();
                } catch (e) {
                    fileErrors.push({
                        entry: line,
                        fileLine: i + 2,
                        mappedKey: "reinstatedProvision",
                        message: `${t("ImportData.InvalidPercentage")}`,
                    });
                    return line;
                }
            }

            return result;
        });

    return { transformedData, fileErrors };
};

export const balanceTransform = (data: TObj[]) => {
    return [...data]
        .filter((line) => !isObjectEmpty(line))
        .map<TObj>((line) => {
            const newBalanceAmount = replaceComma(removeAllSpaces(line.balance as string));
            return {
                ...line,
                balance: Math.round(newBalanceAmount * 100),
            };
        });
};

export const subscriptionTransform = (data: TObj[]) => {
    const fileErrors: Array<{
        entry: (typeof data)[0];
        fileLine: number;
        mappedKey: keyof (typeof data)[0];
        message: string;
    }> = [];

    const transformedData = [...data]
        .filter((line) => !isObjectEmpty(line))
        .map<TObj>((line, i) => {
            const errors: Record<string, string> = {};

            // Validate that either titleId or ean13 is provided
            const hasTitleId = line.titleId && !isNaN(Number(line.titleId));
            const hasEan13 = line.ean13 && String(line.ean13).trim() !== "";

            if (!hasTitleId && !hasEan13) {
                errors.titleId = "ImportData.TitleIdOrEan13Required";
            } else if (hasTitleId && hasEan13) {
                errors.titleId = "ImportData.OnlyOneIdentifierAllowed";
            }

            // Validate consumptionCount (required, must be number)
            if (line.consumptionCount === undefined || line.consumptionCount === null || line.consumptionCount === "") {
                errors.consumptionCount = "ImportData.RequiredField";
            } else if (isNaN(Number(line.consumptionCount))) {
                errors.consumptionCount = "ImportData.InvalidNumber";
            }

            // Validate avgConsumptionDuration (required, must be number)
            if (
                line.avgConsumptionDuration === undefined ||
                line.avgConsumptionDuration === null ||
                line.avgConsumptionDuration === ""
            ) {
                errors.avgConsumptionDuration = "ImportData.RequiredField";
            } else if (isNaN(Number(line.avgConsumptionDuration))) {
                errors.avgConsumptionDuration = "ImportData.InvalidNumber";
            }

            // Add errors if any
            if (Object.keys(errors).length > 0) {
                Object.entries(errors).forEach(([key, message]) => {
                    fileErrors.push({
                        entry: line,
                        fileLine: i + 2, // +2 because Excel starts at 1 and we have a header row
                        mappedKey: key as keyof typeof line,
                        message,
                    });
                });
            }

            return {
                ...(hasTitleId ? { titleId: Number(line.titleId) } : { ean13: String(line.ean13) }),
                consumptionCount: Number(line.consumptionCount || 0),
                avgConsumptionDuration: Number(line.avgConsumptionDuration || 0),
            };
        });

    return { transformedData, fileErrors };
};

/**
 * Removes mandatory field from list of required fields.
 * As we add channelId/date field dynamically to the list, we also need to make sure they're removed.
 * @param field Name of the field removed.
 * @param mandatoryFields List of all the mandatory fields (from useState)
 * @returns Updated list of fields.
 */
export const removeMandatoryField = (field: string, mandatoryFields: string[]) => {
    const index = mandatoryFields.findIndex((item) => item === field);
    return mandatoryFields.splice(index, 1);
};

export const getElementCustomFields = (element: TImportDataDictionary, customFields: ICustomField[]) => {
    if (element === "titles") {
        return customFields.filter((item) => item.relatedResource === "title" && item.valueType !== "fileimport");
    }
    if (element === "beneficiars") {
        return customFields.filter((item) => item.relatedResource === "beneficiary" && item.valueType !== "fileimport");
    }
    if (element === "terms") {
        return customFields.filter((item) => item.relatedResource === "term" && item.valueType !== "fileimport");
    }
    if (element === "subscription") {
        return [];
    }
    return [];
};

type ImportErrorContext = {
    mappedKey?: string;
    value?: unknown;
    channelIndex?: number;
};

/**
 * Column label, repeated-channel slot and rejected value, without the error sentence.
 * An unknown mapped key is left out: the caller passes the file header when it has one.
 */
export const formatImportErrorContext = (err: ImportErrorContext, columnLabels: Record<string, string>) => {
    const column = err.mappedKey ? columnLabels[err.mappedKey] ?? "" : "";
    const channel =
        typeof err.channelIndex === "number" ? t("ImportData.ErrorOnChannel", { index: err.channelIndex }) : "";
    const rejectedValue =
        err.value !== undefined && err.value !== null && String(err.value) !== ""
            ? t("ImportData.ErrorRejectedValue", { value: String(err.value) })
            : "";
    return [column, channel, rejectedValue].filter(Boolean).join(" ");
};

/**
 * Import error line: context, then the message.
 */
export const formatImportErrorMessage = (
    err: ImportErrorContext & { message?: string },
    columnLabels: Record<string, string>,
) => {
    const message = err.message ? t(err.message) : "";
    const context = formatImportErrorContext(err, {
        ...columnLabels,
        ...(err.mappedKey && !columnLabels[err.mappedKey] ? { [err.mappedKey]: err.mappedKey } : {}),
    });
    if (!context) return message;
    return message ? `${context} — ${message}` : context;
};

/**
 * Formats anomalies to download it as an excel file.
 */
export const formatImportAnomalies = ({ errors }: { errors: TImportError[] }) => {
    const excelData: any = { data: [] };

    excelData.columns = [
        {
            header: t("AsyncUploads.FileLine"),
            key: "fileLine",
        },
        {
            header: t("AsyncUploads.Column"),
            key: "mappedKey",
        },
        // Not adding for now. Would mean formatting values according to type (see the formatOldValue in BulkUpload)
        // Maybe later?
        // {
        //     header: t("AsyncUploads.Value"),
        //     key: "value"
        // },
        {
            header: t("AsyncUploads.Message"),
            key: "message",
        },
    ];

    errors.forEach((error) => {
        excelData.data.push({
            fileLine: error.fileLine,
            mappedKey: error.mappedKey,
            message: errorTranslationsWithFallback(error.message),
        });
    });

    return excelData;
};

/**
 * Find the translation according to key sent, if key doesn't exist provide fallback.
 * @param key Translation key wanted
 * @param fallback Default translation if key doesn't exist
 */
export const errorTranslationsWithFallback = (key: string) => {
    return t(key) === key ? errorsWithoutTranslation(key) : t(key);
};

const errorsWithoutTranslation = (message: string) => {
    if (message.includes("field is required")) {
        const element = message.split(" field is required")[0];
        return t("ServerError.Required", { element: t(`ServerError.Element.${element}`) });
    }

    return message;
};

export const getPendingImports = ({
    jobs,
    type,
    fakeImports,
}: {
    jobs: Job[];
    type?: Exclude<TImportTargets, "compute-analytics" | "handle-interforum-files">[];
    fakeImports?: TPendingImport[];
}) => {
    if (fakeImports && fakeImports.length) {
        return type ? fakeImports.filter((item) => type.includes(item.importType)) ?? [] : fakeImports ?? [];
    } else {
        const pendingJobs = jobs.filter(
            (item) =>
                (item.status === ProcessingJobStatus.QUEUED ||
                    item.status === ProcessingJobStatus.PROCESSING ||
                    item.status === ProcessingJobStatus.IDLE) &&
                !["handle-interforum-files", "compute-analytics"].includes(item.importType),
        );
        return type ? pendingJobs.filter((item) => type.includes(item.importType as any)) ?? [] : pendingJobs ?? [];
    }
};
